import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, ExternalLink, Check, Share2, X, MessageCircle, Wallet, CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { MercadoPagoButton } from '../components/payments/MercadoPago';

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, bookingData }) => {
  if (!isOpen || !bookingData) return null;

  const { booking, calendar_links, whatsapp_share_link, credits_remaining } = bookingData;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-background border border-white/10 rounded-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con check animado */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center mb-4 animate-in zoom-in duration-300">
            <Check className="h-10 w-10 text-green-500" strokeWidth={3} />
          </div>
          <h2 className="text-2xl font-bold text-white">¡Reserva Confirmada!</h2>
          <p className="text-white/80 mt-1">Tu turno está listo</p>
        </div>

        {/* Detalles del turno */}
        <div className="p-6">
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-lg">{booking?.day_name}</p>
                  <p className="text-sm text-muted-foreground">{booking?.formatted_date}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">{booking?.time_slot}</p>
                <p className="text-xs text-muted-foreground">hs</p>
              </div>
            </div>
            <div className="pt-3 border-t border-white/10 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Créditos restantes:</span>
              <span className="font-bold text-primary">{credits_remaining}</span>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center mb-2">Añadir a tu calendario:</p>
            
            <div className="grid grid-cols-3 gap-2">
              <a
                href={calendar_links?.google}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-[#4285F4] rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">G</span>
                </div>
                <span className="text-xs">Google</span>
              </a>
              
              <a
                href={calendar_links?.outlook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-[#0078D4] rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">O</span>
                </div>
                <span className="text-xs">Outlook</span>
              </a>
              
              <a
                href={calendar_links?.ical}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs">iCal</span>
              </a>
            </div>

            {/* Compartir con amigo */}
            <a
              href={whatsapp_share_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full p-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-medium">Compartir con un amigo</span>
            </a>
          </div>

          {/* Botón cerrar */}
          <Button 
            onClick={onClose} 
            className="w-full mt-6 bg-primary hover:bg-primary/90"
          >
            Entendido
          </Button>
        </div>
      </div>
    </div>
  );
};

// Buy Credits Modal Component
const BuyCreditsModal = ({ isOpen, onClose, creditPrice, onSuccess }) => {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);

  if (!isOpen) return null;

  const total = creditPrice * quantity;

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const response = await api.post('/wallet/buy-credits', { quantity });
      setPurchaseResult(response.data);
      toast.success('Solicitud de compra registrada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPurchaseResult(null);
    setQuantity(1);
    onClose();
    if (purchaseResult) {
      onSuccess?.();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div 
        className="bg-background border border-white/10 rounded-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary/80 p-6 text-center">
          <div className="w-16 h-16 bg-white rounded-full mx-auto flex items-center justify-center mb-3">
            <Wallet className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-white">Comprar Créditos</h2>
          <p className="text-white/80 text-sm mt-1">1 crédito = 1 hora de práctica</p>
        </div>

        <div className="p-6">
          {!purchaseResult ? (
            <>
              {/* Selector de cantidad */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Cantidad de créditos</label>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-2xl font-bold transition-colors"
                  >
                    -
                  </button>
                  <div className="w-20 text-center">
                    <span className="text-4xl font-bold text-primary">{quantity}</span>
                    <p className="text-xs text-muted-foreground">crédito{quantity > 1 ? 's' : ''}</p>
                  </div>
                  <button
                    onClick={() => setQuantity(Math.min(10, quantity + 1))}
                    disabled={quantity >= 10}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-2xl font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Resumen de precio */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Precio por crédito</span>
                  <span>${creditPrice.toLocaleString('es-AR')}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Cantidad</span>
                  <span>x {quantity}</span>
                </div>
                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-bold">Total</span>
                    <span className="text-2xl font-bold text-primary">${total.toLocaleString('es-AR')}</span>
                  </div>
                </div>
              </div>

              {/* Botón MercadoPago */}
              <MercadoPagoButton 
                type="credits" 
                quantity={quantity} 
                className="w-full mb-3"
              />

              {/* Separador */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 border-t border-white/10"></div>
                <span className="text-xs text-muted-foreground">o pagar por transferencia</span>
                <div className="flex-1 border-t border-white/10"></div>
              </div>

              {/* Botones */}
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose} className="flex-1">
                  Cancelar
                </Button>
                <Button 
                  onClick={handlePurchase} 
                  disabled={loading}
                  variant="outline"
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    'Pedir por Transferencia'
                  )}
                </Button>
              </div>
            </>
          ) : (
            /* Resultado de compra */
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-lg font-bold mb-2">Solicitud Registrada</h3>
              
              <div className="bg-white/5 rounded-xl p-4 mb-4 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Referencia</span>
                  <span className="font-mono text-sm">{purchaseResult.reference}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Créditos</span>
                  <span>{purchaseResult.quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monto</span>
                  <span className="font-bold text-primary">{purchaseResult.formatted_amount}</span>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-yellow-300">
                  <strong>Próximo paso:</strong> Realizá la transferencia y enviá el comprobante al administrador. Una vez confirmado el pago, se acreditarán tus créditos automáticamente.
                </p>
              </div>
              
              <Button onClick={handleClose} className="w-full bg-primary hover:bg-primary/90">
                Entendido
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Booking = () => {
  const { user, refreshUser } = useAuth();
  const [date, setDate] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [wallet, setWallet] = useState({ credits: 0, credit_price: 35000 });

  // Verificar si la fecha seleccionada es hoy
  const isToday = date.toDateString() === new Date().toDateString();

  useEffect(() => {
    fetchWallet();
  }, []);

  useEffect(() => {
    // Limpiar selección al cambiar de fecha
    setSelectedSlot(null);
    fetchAvailability();
  }, [date]);

  const fetchWallet = async () => {
    try {
      const response = await api.get('/wallet');
      setWallet(response.data);
    } catch (error) {
      // Si falla, usar datos del usuario del contexto
      setWallet({ credits: user?.credits || 0, credit_price: 35000 });
    }
  };

  const fetchAvailability = async () => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/bookings/availability?date=${dateStr}`);
      setAvailability(response.data);
    } catch (error) {
      toast.error('Error al cargar disponibilidad');
    }
  };

  const handleBook = async () => {
    if (!selectedSlot) return;

    // Verificar créditos antes de intentar reservar
    if (wallet.credits < 1) {
      toast.error('No tenés créditos suficientes. Comprá créditos para reservar.');
      setShowBuyCreditsModal(true);
      return;
    }

    setLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.post('/bookings', {
        date: dateStr,
        time_slot: selectedSlot
      });
      
      // Guardar resultado y mostrar modal
      setBookingResult(response.data);
      setShowSuccessModal(true);
      
      // Actualizar billetera y limpiar selección
      setWallet(prev => ({ ...prev, credits: response.data.credits_remaining }));
      setSelectedSlot(null);
      fetchAvailability();
      refreshUser?.();
      
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Este horario acaba de ser reservado. Seleccioná otro horario.', { duration: 5000 });
        fetchAvailability();
        setSelectedSlot(null);
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('créditos')) {
        toast.error('No tenés créditos suficientes. Comprá créditos para reservar.', { duration: 5000 });
        setShowBuyCreditsModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Error al reservar turno');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setBookingResult(null);
  };

  const hasCredits = wallet.credits >= 1;

  return (
    <div className="min-h-screen py-20 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="booking-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2">
            Reservar Turno
          </h1>
          <p className="text-muted-foreground">Seleccioná la fecha y el horario que prefieras</p>
        </div>

        {/* Billetera Virtual */}
        <Card className={`glass-effect mb-6 ${hasCredits ? 'border-primary/30' : 'border-yellow-500/30'}`} data-testid="wallet-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${hasCredits ? 'bg-primary/20' : 'bg-yellow-500/20'}`}>
                  <Wallet className={`h-6 w-6 ${hasCredits ? 'text-primary' : 'text-yellow-500'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tu billetera</p>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-3xl font-bold ${hasCredits ? 'text-primary' : 'text-yellow-500'}`} data-testid="wallet-credits">
                      {wallet.credits}
                    </span>
                    <span className="text-muted-foreground">crédito{wallet.credits !== 1 ? 's' : ''} disponible{wallet.credits !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => setShowBuyCreditsModal(true)}
                variant={hasCredits ? "outline" : "default"}
                className={`w-full sm:w-auto ${!hasCredits ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : ''}`}
                data-testid="buy-credits-button"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {hasCredits ? 'Comprar más créditos' : 'Comprar créditos'}
              </Button>
            </div>
            
            {!hasCredits && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-300 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  Necesitás al menos 1 crédito para reservar un turno. Cada crédito cuesta ${wallet.credit_price?.toLocaleString('es-AR')}.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Calendario */}
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Fecha
              </CardTitle>
              <CardDescription>Elegí el día de tu práctica</CardDescription>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(newDate) => newDate && setDate(newDate)}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-md border border-white/10"
                data-testid="booking-calendar"
              />
            </CardContent>
          </Card>

          {/* Horarios disponibles */}
          <Card className="glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horarios Disponibles
              </CardTitle>
              <CardDescription>
                {date.toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {isToday && (
                  <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary rounded text-xs">Hoy</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Mensaje informativo para el día de hoy */}
              {isToday && availability.some(slot => slot.passed) && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                  <p className="text-blue-300">
                    <Clock className="h-4 w-4 inline mr-2" />
                    Solo se muestran los horarios que aún no han comenzado
                  </p>
                </div>
              )}
              
              {/* Filtrar slots: solo mostrar los disponibles y los no disponibles que NO han pasado */}
              {availability.filter(slot => !slot.passed).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-semibold">No hay horarios disponibles para hoy</p>
                  <p className="text-sm mt-2">Seleccioná otra fecha para ver los turnos disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {availability.filter(slot => !slot.passed).map((slot) => {
                    const isSelected = selectedSlot === slot.time_slot;
                    const isAvailable = slot.available;
                    const isBlocked = slot.blocked;
                    
                    return (
                      <button
                        key={slot.time_slot}
                        onClick={() => isAvailable && setSelectedSlot(slot.time_slot)}
                        disabled={!isAvailable}
                        className={`p-5 sm:p-4 rounded-xl border-2 transition-all text-center min-h-[80px] sm:min-h-[72px] active:scale-95 touch-manipulation ${
                          isSelected
                            ? 'bg-primary text-primary-foreground border-primary shadow-lg scale-[1.02]'
                            : isAvailable
                            ? 'bg-white/5 border-white/20 hover:border-primary/50 hover:bg-white/10 active:bg-primary/20'
                            : isBlocked
                            ? 'bg-red-500/10 border-red-500/30 opacity-60 cursor-not-allowed'
                            : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                        }`}
                        data-testid={`slot-${slot.time_slot}`}
                      >
                        <p className="text-xl sm:text-lg font-bold">{slot.time_slot}</p>
                        <p className={`text-sm sm:text-xs mt-1 ${isBlocked ? 'text-red-400' : ''}`}>
                          {isAvailable ? 'Disponible' : isBlocked ? (slot.block_reason || 'No disponible') : 'Ocupado'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedSlot && (
                <div className={`mt-6 p-4 sm:p-6 rounded-xl ${hasCredits ? 'bg-primary/10 border border-primary/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`} data-testid="selected-slot-info">
                  <p className="font-semibold mb-2 text-lg">Turno Seleccionado:</p>
                  <p className="text-base sm:text-sm text-muted-foreground mb-4">
                    {date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })} - {selectedSlot} hs
                  </p>
                  
                  {hasCredits ? (
                    <>
                      <Button
                        onClick={handleBook}
                        disabled={loading}
                        className="w-full h-14 sm:h-12 text-lg sm:text-base bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider active:scale-[0.98] touch-manipulation transition-transform"
                        data-testid="confirm-booking-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Reservando...
                          </>
                        ) : (
                          'Confirmar Reserva'
                        )}
                      </Button>
                      <p className="text-sm sm:text-xs text-muted-foreground mt-3 text-center">
                        Se descontará 1 crédito de tu cuenta
                      </p>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowBuyCreditsModal(true)}
                        className="w-full h-14 sm:h-12 text-lg sm:text-base bg-yellow-500 hover:bg-yellow-600 text-black font-bold uppercase tracking-wider active:scale-[0.98] touch-manipulation transition-transform"
                        data-testid="buy-credits-to-book-button"
                      >
                        <CreditCard className="h-5 w-5 mr-2" />
                        Comprar Créditos para Reservar
                      </Button>
                      <p className="text-sm sm:text-xs text-yellow-300 mt-3 text-center">
                        Necesitás créditos para confirmar esta reserva
                      </p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        bookingData={bookingResult}
      />

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        creditPrice={wallet.credit_price}
        onSuccess={fetchWallet}
      />
    </div>
  );
};
